import React, { useCallback, useEffect, useRef, useState } from "react";
import { PlusIcon, MinusIcon } from "../../../Icons";

/**
 * Compact +/− toggle used by every patient section / role card.
 * Matches Authorizations header control.
 */
export const CollapseToggle = ({
  open,
  onToggle,
  labelOpen = "Collapse",
  labelClosed = "Expand",
  size = "md",
  className = "",
}) => {
  const box =
    size === "sm"
      ? "h-6 w-6 rounded border"
      : "h-7 w-7 rounded-md border";
  const iconSize = size === "sm" ? 12 : 14;

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onToggle?.();
      }}
      className={`inline-flex items-center justify-center bg-white border-slate-300 text-slate-600 hover:bg-slate-50 hover:text-slate-800 transition-colors shrink-0 ${box} ${className}`}
      aria-expanded={open}
      aria-label={open ? labelOpen : labelClosed}
      title={open ? labelOpen : labelClosed}
    >
      {open ? <MinusIcon size={iconSize} /> : <PlusIcon size={iconSize} />}
    </button>
  );
};

/**
 * Open when the section has items; stay collapsed when empty.
 * Resets when `resetKey` changes (patient / chart switch).
 * Manual +/- still works until the next reset / empty sync.
 */
export const useCollapseOpenForCount = (itemCount, resetKey = "") => {
  const [open, setOpen] = useState(false);
  const userOverrodeRef = useRef(false);

  useEffect(() => {
    userOverrodeRef.current = false;
    setOpen(false);
  }, [resetKey]);

  useEffect(() => {
    const count = Number(itemCount) || 0;
    if (count <= 0) {
      setOpen(false);
      return;
    }
    if (!userOverrodeRef.current) setOpen(true);
  }, [itemCount]);

  const onOpenChange = useCallback((next) => {
    userOverrodeRef.current = true;
    setOpen(next);
  }, []);

  return [open, onOpenChange];
};

/**
 * Reusable collapsible section card for patient tabs.
 *
 * Header layout: [+/−] | bar · icon · TITLE · badge ·········· actions
 */
const CollapsibleSection = ({
  title,
  subtitle,
  icon,
  tone = {},
  badge,
  actions,
  defaultOpen = false,
  open: openProp,
  onOpenChange,
  children,
  className = "",
  bodyClassName = "",
  headerClassName = "",
  collapseLabel,
}) => {
  const isControlled = openProp !== undefined;
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const open = isControlled ? openProp : internalOpen;

  const setOpen = useCallback(
    (next) => {
      const value = typeof next === "function" ? next(open) : next;
      if (!isControlled) setInternalOpen(value);
      onOpenChange?.(value);
    },
    [isControlled, onOpenChange, open],
  );

  const wrap = tone.wrap || "border-slate-200";
  const head = tone.head || "bg-slate-50 border-slate-100";
  const bar = tone.bar || "bg-slate-500";
  const iconTone = tone.icon || "text-slate-600";
  const titleTone = tone.title || "text-slate-800";

  const label = collapseLabel || title || "section";

  return (
    <section
      className={`bg-white rounded-xl border shadow-sm overflow-hidden ${wrap} ${className}`}
    >
      <div
        className={`flex items-center gap-2 h-11 px-4 border-b box-border rounded-t-xl ${head} ${
          open ? "" : "rounded-b-xl border-b-0"
        } ${headerClassName}`}
      >
        <CollapseToggle
          open={open}
          onToggle={() => setOpen((o) => !o)}
          labelOpen={`Collapse ${label}`}
          labelClosed={`Expand ${label}`}
        />
        <span className={`w-0.5 h-4 rounded-full shrink-0 ${bar}`} aria-hidden />
        {icon ? (
          <span className={`shrink-0 ${iconTone}`}>{icon}</span>
        ) : null}
        <div className="min-w-0 flex-1 flex items-baseline gap-2 overflow-hidden">
          <h3
            className={`text-xs font-semibold uppercase tracking-wide truncate ${titleTone}`}
          >
            {title}
          </h3>
          {subtitle ? (
            <span className="text-[10px] italic font-normal normal-case tracking-normal text-slate-500 truncate hidden sm:inline">
              {subtitle}
            </span>
          ) : null}
        </div>
        {badge ? <div className="shrink-0 flex items-center">{badge}</div> : null}
        {actions ? (
          <div className="flex items-center gap-2 shrink-0 h-7">{actions}</div>
        ) : null}
      </div>
      {open ? (
        <div className={`bg-white rounded-b-xl ${bodyClassName}`}>{children}</div>
      ) : null}
    </section>
  );
};

export default CollapsibleSection;
